import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import Input from "../../components/ui/Input";
import Button from "../../components/ui/Button";

const errorText = (err, fallback) => {
  const errors = err?.response?.data?.errors;
  return (errors && Object.values(errors)[0]?.[0]) || err?.response?.data?.message || fallback;
};

const EMAIL_PREFS = [
  { key: "email_enabled", label: "Booking emails", hint: "Confirmations, reminders, receipts, changes and cancellations." },
  { key: "sms_enabled", label: "Booking texts (SMS)", hint: "Sent to your phone number above. Rwandan numbers only (078, 079, 072, 073)." },
  { key: "push_enabled", label: "Push notifications", hint: "On your phone, from the SHUWA app." },
  { key: "marketing_partner_opt_in", label: "Offers from places I've booked", hint: "Deals and news from businesses you've visited." },
];

export default function Profile() {
  const { user, setUser, deleteAccount } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: user.full_name,
    email: user.email || "",
    phone: user.phone || "",
    two_factor_enabled: Boolean(user.two_factor_enabled),
  });
  const [saving, setSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState(null);
  const [passwords, setPasswords] = useState({ current_password: "", password: "", password_confirmation: "" });
  const [passwordMsg, setPasswordMsg] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/account/notification-preferences").then(({ data }) => setPrefs(data)).catch(() => {});
  }, []);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    setProfileMsg(null);
    try {
      const { data } = await api.put("/account", { ...form, email: form.email || null, phone: form.phone || null });
      setUser(data);
      setForm((f) => ({ ...f, phone: data.phone || "", email: data.email || "" }));
      setProfileMsg({ ok: true, text: "Saved." });
    } catch (err) {
      setProfileMsg({ ok: false, text: errorText(err, "Could not save your changes.") });
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPasswordMsg(null);
    try {
      await api.put("/account/password", passwords);
      setPasswords({ current_password: "", password: "", password_confirmation: "" });
      setPasswordMsg({ ok: true, text: "Password updated. You've been signed out on your other devices." });
    } catch (err) {
      setPasswordMsg({ ok: false, text: errorText(err, "Could not update your password.") });
    }
  };

  const togglePref = async (key) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    try {
      const { data } = await api.put("/account/notification-preferences", { [key]: next[key] });
      setPrefs(data);
    } catch {
      setPrefs(prefs);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setError("");
    try {
      await deleteAccount();
      navigate("/");
    } catch (err) {
      setError(err?.response?.data?.message || "Could not delete your account. Please try again or contact support@shuwa.app.");
      setDeleting(false);
    }
  };

  const Message = ({ msg }) =>
    msg ? <p className={`text-sm ${msg.ok ? "text-emerald-700" : "text-red-600"}`}>{msg.text}</p> : null;

  return (
    <div className="max-w-lg">
      <h1 className="mb-4 font-montserrat text-2xl font-bold text-slate-900">Profile</h1>
      <form onSubmit={saveProfile} className="space-y-4">
        <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Full name" required />
        <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" />
        <Input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone (e.g. 0788 123 456)" />
        <label className="flex items-start gap-3 text-sm">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 accent-primary"
            checked={form.two_factor_enabled}
            onChange={(e) => setForm({ ...form, two_factor_enabled: e.target.checked })}
          />
          <span>
            <span className="font-medium text-slate-900">Two-step login</span>
            <span className="block text-muted-foreground">We'll email you a code each time you sign in with your password.</span>
          </span>
        </label>
        <Message msg={profileMsg} />
        <Button type="submit" disabled={saving}>{saving ? "Saving…" : "Save changes"}</Button>
      </form>

      <div className="mt-10 border-t border-border pt-6">
        <h2 className="mb-1 font-montserrat text-lg font-bold text-slate-900">Notifications</h2>
        <p className="mb-4 text-sm text-muted-foreground">
          {form.email ? <>Emails go to <strong>{user.email || form.email}</strong>.</> : "Add an email above to get booking emails."}{" "}
          Login and password emails are always sent.
        </p>
        {prefs ? (
          <div className="space-y-3">
            {EMAIL_PREFS.map((p) => (
              <label key={p.key} className="flex items-start gap-3 text-sm">
                <input type="checkbox" className="mt-1 h-4 w-4 accent-primary" checked={Boolean(prefs[p.key])} onChange={() => togglePref(p.key)} />
                <span>
                  <span className="font-medium text-slate-900">{p.label}</span>
                  <span className="block text-muted-foreground">{p.hint}</span>
                </span>
              </label>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Loading…</p>
        )}
      </div>

      <div className="mt-10 border-t border-border pt-6">
        <h2 className="mb-4 font-montserrat text-lg font-bold text-slate-900">Password</h2>
        <form onSubmit={changePassword} className="space-y-3">
          <Input
            type="password"
            autoComplete="current-password"
            value={passwords.current_password}
            onChange={(e) => setPasswords({ ...passwords, current_password: e.target.value })}
            placeholder="Current password"
          />
          <Input
            type="password"
            autoComplete="new-password"
            minLength={8}
            required
            value={passwords.password}
            onChange={(e) => setPasswords({ ...passwords, password: e.target.value })}
            placeholder="New password (at least 8 characters)"
          />
          <Input
            type="password"
            autoComplete="new-password"
            required
            value={passwords.password_confirmation}
            onChange={(e) => setPasswords({ ...passwords, password_confirmation: e.target.value })}
            placeholder="Confirm new password"
          />
          <Message msg={passwordMsg} />
          <Button type="submit" variant="outline">Update password</Button>
        </form>
      </div>

      <div className="mt-10 border-t border-border pt-6">
        <h2 className="mb-2 font-montserrat text-lg font-bold text-slate-900">Delete account</h2>
        <p className="mb-4 text-sm text-muted-foreground">
          Permanently deletes your profile, favorites, loyalty cards, gift cards, and family &amp; friends
          connections. This can't be undone.{" "}
          <a href="/delete-account" className="underline">
            Learn what's kept and why.
          </a>
        </p>
        {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
        {!confirming ? (
          <Button variant="danger" onClick={() => setConfirming(true)}>Delete my account</Button>
        ) : (
          <div className="space-y-3 rounded-lg border border-red-200 bg-red-50 p-4">
            <p className="text-sm font-medium text-red-800">Are you sure? This will permanently delete your account.</p>
            <div className="flex gap-3">
              <Button variant="danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? "Deleting…" : "Yes, delete it"}
              </Button>
              <Button variant="outline" onClick={() => setConfirming(false)} disabled={deleting}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
